import React, { useEffect, useState, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SCHEMALINK_URL } from "./schemalink";
import {
    ControlsContainer,
    ZoomControl,
    FullScreenControl,
    SigmaContainer,

} from "@react-sigma/core";
import "@react-sigma/core/lib/react-sigma.min.css";
import { IoMdSettings } from "react-icons/io";
import { FaPaperPlane, FaRedo, FaSearch } from "react-icons/fa"; //FaArrowAltCircleDown, FaArrowRight, FaArrowDown
import { EdgeLineProgram, EdgeArrowProgram } from "sigma/rendering";
import { EdgeCurvedArrowProgram } from "@sigma/edge-curve"; //EdgeCurveProgram,
//import { Tabs, Tab, Box, Typography } from '@mui/material';
import { MultiGraph } from "graphology";
import * as d3 from "d3";
import "../style/index.css";
import LoadGraph from "./LoadGraph";
import MenuGraph from "./MenuGraph";
import MenuOptions from "./MenuOptions";
import CustomForm1 from "./CustomForm1";
import CustomForm2 from "./CustomForm2";
import CustomForm3 from "./CustomForm3";
import CustomForm4 from "./CustomForm4";
import ModalArticles from "./ModalArticles";
import "bootstrap/dist/js/bootstrap.bundle.min";
import html2canvas from "html2canvas";

function DisplayGraph() {

    // Plain white page behind the graph (see .display-graph-page in index.css);
    // removed again when leaving, so the homepage keeps its own background.
    useEffect(() => {
        document.body.classList.add('display-graph-page');
        return () => document.body.classList.remove('display-graph-page');
    }, []);

    const [updateData, updateNewData] = useState({});
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [lastModifiedVariable, setLastModifiedVariable] = useState(null); //determine if an arc or node was passed last
    const [graph, setGraph] = useState(new MultiGraph());
    const [familyColor, setFamilyColor] = useState({}); //allows you to change the color of nodes based on the family in a real time
    const [familyLabel, setFamilyLabel] = useState({});
    const [familyConfidence, setFamilyConfidence] = useState({});
    const [numberFamilyConfidence, setNumberFamilyConfidence] = useState({});
    const [viewFamily, setViewFamily] = useState({}); //allows you to view nodes of selected families real-time
    const [viewEdges, setViewEdges] = useState({}); //allows you to view edges of selected families real-time
    const [acceptConfidentEdges, setAcceptConfidentEdges] = useState({});
    const [confidenceLevel, setConfidenceLevel] = useState(0.0);
    const [pressButton, setPressButton] = useState(null);
    const [isMenuOptionsOpen, setMenuOptionsOpen] = useState(false); //handle menu options open/close
    const [isMenuGraphOpen, setMenuGraphOpen] = useState(false); //handle menu graph open/close
    const [informationGraph, setInformationGraph] = useState({}); //information of graph
    const [confidenceEdges, setConfidenceEdges] = useState(0); //information of edges
    const [markedEdges, setMarkedEdges] = useState(0); //information of edges
    const [selectedLayout, setSelectedLayout] = useState("normal");
    const [selectedNodeSize, setSelectedNodeSize] = useState(8);
    const [selectedEdgeSize, setSelectedEdgeSize] = useState(5);
    const [viewEdgesForFile, setViewEdgesForFile] = useState([]);
    const [viewEdgesNoConfForFile, setViewEdgesNoConfForFile] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [abstractData, setAbstractData] = useState([]);
    const [enterButton, setEnterButton] = useState(true);
    const [ratedEdgeIds, setRatedEdgeIds] = useState([]);
    const [totalEdges, setTotalEdges] = useState(0);
    const [regEx, setRegEx] = useState();
    const [parts, setParts] = useState([]);
    const [ontologyLabels, setOntologyLabels] = useState([]);
    const [uniquePred, setUniquePred] = useState([]);
    const [isPred, setIsPred] = useState(false);
    const [colorMap, setColorMap] = useState({});
    const [nodeType, setNodeType] = useState({});
    const [currPage, setCurrPage] = useState(1);
    const [italicSpans, setItalicSpans] = useState();

    const loadRef = useRef(null);

    const navigate = useNavigate();

    // Getting data from ModalArticles
    const location = useLocation();
    const stateData = location.state;
    const abstract = stateData['abstract'];
    const tableData2 = stateData['tableData2'];
    const visMethod = stateData['visMethod'];
    const Data = stateData['tripleData'];
    // console.log('graph json:\n', Data);

    let newData;
    let graph_num;
    // check for multiple graph
    if (Object.keys(Data).includes('graph0')) {
        graph_num = Object.keys(Data).length
    }
    

    let ontology_labels = [];
    let unique_pred = [];
    let merged_spans = [];

    useEffect(() => {
        // Count ratable edges -- those with a defined, non-null _confidence
        // above the current confidenceLevel threshold (i.e. not hidden), other
        // than the ones already in the KG (confidence 1) --
        // across every graph/page in Data, not just the one currently shown.
        // Recomputed whenever confidenceLevel changes, since raising/lowering
        // the threshold changes which edges are hidden.
        const graphs = Object.keys(Data).includes('graph0')
            ? Object.keys(Data).map((g) => Data[g])
            : [Data];

        // Step 1: mirror LoadGraph's per-page construction dedup -- edges
        // sharing the same (start, end, normalized label), direction-agnostic,
        // are duplicates within a page and only the first one is ever added
        // to that page's graph.
        const survivingEdges = [];
        for (const g of graphs) {
            const seenKeys = new Set();
            for (const edge of g['result_edges']) {
                const labelNormalized = edge['label'].replace(/_/g, '').toLowerCase();
                const key = [edge['start'], edge['end'], labelNormalized].sort().join('_');
                if (seenKeys.has(key)) {
                    continue; // duplicate edge, LoadGraph skips it too
                }
                seenKeys.add(key);
                survivingEdges.push(edge);
            }
        }

        // Step 2: mirror the rating functions' own dedup rule (e.g.
        // veryGoodRateConfidence in LoadGraph) -- edges are counted as the
        // same rating by (start, label, target) alone, direction-sensitive,
        // shared globally across ALL pages via the ratedEdges state. So an
        // edge repeating the same triple on a later page doesn't add a new
        // rating even though it's a distinct edge object.
        const ratedDedupKeys = new Set();
        let total = 0;
        for (const edge of survivingEdges) {
            const confidence = edge['properties']['_confidence'];
            if (confidence === undefined || confidence === null || confidence <= confidenceLevel) {
                continue;
            }
            // A confidence of exactly 1 marks an edge already in the KG (drawn
            // black) -- a known fact, not a prediction, so it can't be rated
            // (MenuEdge offers no "Rate Edge" and the bulk rating actions skip
            // it) and isn't counted here either.
            if (confidence === 1) {
                continue;
            }
            const label = edge['label'].replace(/_/g, ' ');
            const ratingKey = `${edge['start']}${label}${edge['end']}`;
            if (!ratedDedupKeys.has(ratingKey)) {
                ratedDedupKeys.add(ratingKey);
                total += 1;
            }
        }
        setTotalEdges(total);
    }, [confidenceLevel]);

    useEffect(() => {
        let spans = [];
        if (Object.keys(Data).includes('graph0')) {
            newData = Data['graph0']; 
            console.log('Graphs from backend:\n', Data);
        }
        else {
            newData = Data;
            console.log('newData: \n', newData);
        }

        updateNewData(newData);
        let uri_label_obj = {};
        newData['result_nodes'].forEach(node => {
            uri_label_obj[node['properties']['URI']] = node['properties']['Label']
            // if (node['properties']['Synonyms'] !== undefined){
            //     ontology_labels.push(node['properties']['Synonyms']);
            // }
        });

        console.log('uri labels:', '\n', uri_label_obj)
        console.log('This is tableData2', '\n', tableData2);

        tableData2['data'].forEach(row => {
            // subject
            if (uri_label_obj[row[0]] !== undefined) ontology_labels.push(uri_label_obj[row[0]]);
            // object
            if (uri_label_obj[row[2]] !== undefined) ontology_labels.push(uri_label_obj[row[2]]);
            // predicate
            unique_pred.push(row[1].replaceAll('_', ' '))
            ontology_labels.push(row[1].replaceAll('_', ' '))
            // synonyms
            // ontology_labels.push(uri_labels_obj[])

            // Spans -- only meaningful for triples that came from a source
            // abstract (putative_span is a "start:end" character offset into
            // that text). schemalink-sourced triples have no source abstract,
            // so putative_span is always null there; skip those instead of
            // crashing on null.split(':').
            if (row[5] != null) spans.push(row[5]);

        });

        const ontology_labels_set = new Set(ontology_labels);
        const unique_pred_set = new Set(unique_pred);
        console.log(ontology_labels_set);
        const regex = new RegExp(`(${ontology_labels.join('|')})`, 'gi');

        // The abstract-highlighting panel (spans + text splitting) only applies
        // when this graph actually came from a source abstract -- schemalink-
        // sourced graphs have abstract === null, so there's nothing to anchor
        // spans to or split. Leave that panel empty instead of crashing.
        if (typeof abstract === 'string') {
            // get unique spans and sort
            let u_spans = [];
            for (let i=0;i < spans.length;i++) {
                if (!u_spans.includes(spans[i])) u_spans.push([parseInt(spans[i].split(':')[0]), parseInt(spans[i].split(':')[1])])
            }
            // merge spans if overlaped -- schemalink now sends real source
            // text but still no putative_span, so u_spans is legitimately
            // empty here even though abstract is a real string; merge_spans([])
            // returns [undefined] (reads sorted_spans[0] unconditionally),
            // which crashes renderStyledText's `span[0]` access downstream.
            // Skip straight to [] instead -- text still renders, just with
            // no italicized spans, which is correct: there's nothing to
            // italicize without span data.
            merged_spans = u_spans.length > 0 ? merge_spans(u_spans.sort()) : [];
            setItalicSpans(merged_spans);
            console.log('merged spans:\n', merged_spans);

            setParts(abstract.split(regex));
            const txt_parts = abstract.split(regex);
            for (let up of uniquePred) {
                if (txt_parts.includes(up)) {
                    setIsPred(true);
                    console.log('predicate exist in text\n', up);
                    break;
                }
            }
        } else {
            setItalicSpans([]);
            setParts([]);
        }
        setRegEx(regex);
        setOntologyLabels(ontology_labels);
        setUniquePred(unique_pred_set);


        const familyColorMap = {};
        const cm = {}
        const nt = {}
        newData["result_nodes"].forEach((myNode) => {
          nt[myNode['properties']['Label']] = myNode['labels']
          myNode.labels.forEach((family) => {
            if (!familyColorMap[family]) {
              const color = d3.schemeCategory10[Object.keys(familyColorMap).length % 10];
              familyColorMap[family] = color;

            }
            cm[myNode['properties']['Label']] = familyColorMap[family]
          });
        });
        setColorMap(cm);
        setNodeType(nt);
        

    }, [newData]);

    const merge_spans = (sorted_spans) => {
        let merged = [sorted_spans[0]];
        for (let i = 1; i < sorted_spans.length; i++) {
            const prev = merged[merged.length - 1];
            const curr = sorted_spans[i];

            if (curr[0] <= prev[1]) {
                // Extend the previous span
                prev[1] = Math.max(prev[1], curr[1]);
            } else {
                merged.push(curr);
            }
        }
        return merged;
    }

    function renderHighlights(segment, highlightWords, keyOffset = 0) {
        if (!highlightWords.length) return segment;
        // Escape special regex chars in words to avoid errors
        const escapeRegex = (word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const pattern = new RegExp(`(${highlightWords.map(escapeRegex).join("|")})`, "gi");

        const pieces = segment.split(pattern);

        return pieces.map((piece, i) =>
            highlightWords.some((w) => w.toLowerCase() === piece.toLowerCase()) ? (
            <span key={`${keyOffset}-${i}`} title={nodeType[piece].join(' | ').replaceAll('_', ' ')} className='highlight' style={{ backgroundColor: colorMap[piece] }}>{piece}</span>
            ) : (
            <React.Fragment key={`${keyOffset}-${i}`}>{piece}</React.Fragment>
            )
        );
    }

    function renderStyledText(text, mergedSpans, highlightWords) {
        const parts = [];
        let lastIndex = 0;

        for (const span of mergedSpans) {
            let start = span[0];
            let end = span[1];
            if (start > lastIndex) {
                parts.push(renderHighlights(text.slice(lastIndex, start), highlightWords, lastIndex));
            }

            parts.push(
                <i key={start} style={{color: 'red'}}>
                {renderHighlights(text.slice(start, end), highlightWords, start)}
                </i>
            );

            lastIndex = end;
        }

        if (lastIndex < text.length) {
            parts.push(renderHighlights(text.slice(lastIndex), highlightWords, lastIndex));
        }

    return <>{parts}</>;
  }

    const handleNextPage = (e) => {
        e.preventDefault();
        console.log('Current page:\n', currPage);
        if (currPage + 1 <= graph_num) {
            // Shallow-clone so this is always a new object reference, even on
            // a revisit to a page already seen -- otherwise LoadGraph's graph
            // construction effect (keyed on dataGraph reference) silently
            // skips rebuilding, leaving a stale graph (e.g. missing rating
            // labels applied since the last visit) on screen.
            updateNewData({ ...Data['graph' + currPage.toString()] });
            setCurrPage(currPage + 1);
        }
    };

    const handleNumPage = (e) => {
        e.preventDefault()
        console.log('Current page:\n', currPage);
        let page = parseInt(e.currentTarget.textContent);
        updateNewData({ ...Data['graph' + (page - 1).toString()] });
        setCurrPage(page);

    }

    const handlePrevPage = (e) => {
        e.preventDefault();
        console.log('Current page:\n', currPage);
        if (currPage - 1 != 0) {
            updateNewData({ ...Data['graph' + (currPage - 2).toString()] });
            setCurrPage(currPage - 1);
        }
    };


    const handleVeryGoodConfidence = () => {
        loadRef.current.veryGoodRateConfidence();
    };

    const handleGoodConfidence = () => {
        loadRef.current.goodRateConfidence();
    };

    const handleBadConfidence = () => {
        loadRef.current.badRateConfidence();
    };

    const handleVeryBadConfidence = () => {
        loadRef.current.veryBadRateConfidence();
    };

    const handleLayoutChange = () => {
        setSelectedLayout((prevLayout) =>
            prevLayout === "fa2" ? "normal" : "fa2"
        );
    };

    //determine if an arc or node was passed last (it's required for menù attribute)
    useEffect(() => {
        if (selectedNode !== null && selectedNode !== lastModifiedVariable) {
            setLastModifiedVariable(selectedNode);
            setSelectedEdge(null);
        } else if (
            selectedEdge !== null &&
            selectedEdge !== lastModifiedVariable
        ) {
            setLastModifiedVariable(selectedEdge);
            setSelectedNode(null);
        }
    }, [selectedNode, selectedEdge, lastModifiedVariable]);

    //- - - - - - - - - - - - - - INPUTTEXT parameters - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    const handleInputChange = (event) => {
        //do somethings with input text value
        setInputValue(event.target.value);
    };

    const handleEnterClick = async () => {
        const data = await getAbstract();
        setAbstractData(data);
        console.log(abstractData);
        if (enterButton === true) {
            setEnterButton(false);
        } else {
            setEnterButton(true);
        }
    };

    const refreshPage = async () => {
        window.location.reload();
    };

    const getAbstract = async () => {
        const query = inputValue.split(" ").join(" AND ");
        try {
            const api_query = `https://bio-viber.biodata.di.unimi.it/api/pubmed_gathering?query="${query}"&max=10`;
            const response = await fetch(api_query);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            const data = "Error";
            console.log(error.message);
            return data;
        }
    };

    //- - - - - - - - - - - - - - SAVE EDGES - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    const handleButtonClick = async () => {
        try {
            const acceptedEdges = [];
            let edgesAsString = ""; // Declare with let so it can be reassigned
            // console.log("viewEdgesForFile", viewEdgesForFile);
            // console.log("viewEdgesNoConfForFile",viewEdgesNoConfForFile);
            viewEdgesForFile.forEach((edgeWithConfidence) => {
                console.log(edgeWithConfidence);
                viewEdgesNoConfForFile.forEach((edge) => {
                    if (
                        edgeWithConfidence.identity.toString() === edge.identity
                    ) {
                        if (edge.properties.accept === true) {
                            acceptedEdges.push(edgeWithConfidence);
                        }
                    }
                });
            });

            // Construct edgesAsString from acceptedEdges
            edgesAsString =
                "[" +
                acceptedEdges.map((edge) => JSON.stringify(edge)).join(",") +
                "]";

            const response = await fetch(
                "https://bio-viber.biodata.di.unimi.it/api/store_graph",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: edgesAsString,
                }
            );

            // const file = new Blob([edgesAsString], { type: 'text/plain;charset=utf-8' });  // Create file from edgesAsString
            // console.log(edgesAsString);
            // saveAs(file, 'accept_edges.json');  // Save file

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();
            console.log("Success:", data);
        } catch (error) {
            console.log("Problem:" + error);
        }
    };

    //- - - - - - - - - - - - - - SAVE EDGES - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    const handleButtonClickEnhance = async () => {
        try {
            const acceptedEdges = [];
            let edgesAsString = ""; // Declare with let so it can be reassigned
            // console.log("viewEdgesForFile", viewEdgesForFile);
            // console.log("viewEdgesNoConfForFile",viewEdgesNoConfForFile);
            viewEdgesForFile.forEach((edgeWithConfidence) => {
                console.log(edgeWithConfidence);
                viewEdgesNoConfForFile.forEach((edge) => {
                    if (
                        edgeWithConfidence.identity.toString() === edge.identity
                    ) {
                        if (
                            edge.properties.accept === true ||
                            edge.properties.declined === true
                        ) {
                            if (edge.properties.declined === true) {
                                edgeWithConfidence.properties.declined = true;
                                console.log("trovato declinato", edge);
                            }
                            acceptedEdges.push(edgeWithConfidence);
                        }
                    }
                });
            });

            // Construct edgesAsString from acceptedEdges
            edgesAsString =
                "[" +
                acceptedEdges.map((edge) => JSON.stringify(edge)).join(",") +
                "]";

            const response = await fetch(
                "https://bio-viber.biodata.di.unimi.it/api/enhance_schema",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: edgesAsString,
                }
            );

            // const file = new Blob([edgesAsString], { type: 'text/plain;charset=utf-8' });  // Create file from edgesAsString
            // console.log(edgesAsString);
            // saveAs(file, 'action_edges.json');  // Save file

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();
            console.log("Success:", data);
        } catch (error) {
            console.log("Problem:" + error);
        }
    };

    //- - - - - - - - - - - - - - MENUOPTIONS parameters - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    const toggleMenuOptions = () => {
        setMenuOptionsOpen(!isMenuOptionsOpen);
    };

    const menuItems = [
        {
            id: "family",
            label: "Node Types",
            content: (
                <CustomForm1
                    colorOfMyFamilys={familyColor}
                    onColorOfMyFamilysChange={setFamilyColor}
                    choiceViewFamily={setViewFamily}
                />
            ), //I pass the family-color object and the function to modify the family-color object: through onColorOfMyFamilysChange I can take back the color of the family modified in CustomForm1. Also pass the object {family: see/don’t see}
        },
        {
            id: "confidence",
            label: "Confidence of Edges",
            content: (
                <CustomForm2
                    confidenceLevelUser={setConfidenceLevel}
                    labelOfMyEdges={familyConfidence}
                    numerEdge={numberFamilyConfidence}
                    acceptEdgePredicted={setAcceptConfidentEdges}
                    buttonPressed={setPressButton}
                    veryGoodConfidenceRate={handleVeryGoodConfidence}
                    goodConfidenceRate={handleGoodConfidence}
                    badConfidenceRate={handleBadConfidence}
                    veryBadConfidenceRate={handleVeryBadConfidence}
                />
            ), //get the value of confidence set by user
        },
        {
            id: "edges",
            label: "Edge Types",
            content: (
                <CustomForm3
                    labelOfMyEdges={familyLabel}
                    viewEdgesPrediction={setViewEdges}
                />
            ),
        },
        {
            id: "sizes",
            label: "Size of nodes and edges",
            content: (
                <CustomForm4
                    onChangeNodeSize={setSelectedNodeSize}
                    onChangeEdgeSize={setSelectedEdgeSize}
                />
            ),
        },
    ];

    const prova = (newData) => {
        if (newData === "Error") {
            console.log("ERROR");
        }

        console.log(newData);
        updateNewData(newData);
        // Example of state update with the new data
        // setDati(newData);
    };

    const handleExit = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        for (let i = 0;i < 2;i++) {
            backdrops[i].remove();
        } 
        document.body.classList.remove("modal-open");
        document.body.style = ""; 
        // Back to Schema Link -- Bio-Viber's own homepage isn't for users
        window.location.href = SCHEMALINK_URL;
    };

    const handleReturn = () => {
        console.log(ratedEdgeIds);
    }

    const isHead = isMenuOptionsOpen && isMenuGraphOpen;

    
    return (
        <div className="display-container">

            <div className="modal fade" id="exitModal" tabIndex="-1" aria-labelledby="exitModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                    <div className="modal-body" style={{margin: "2rem 0", textAlign: "center"}}>
                        You have rated <strong>{ratedEdgeIds.length}</strong> out of <strong>{totalEdges}</strong> edges. Do you want to proceed

                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" onClick={handleReturn}>Return</button>
                        <button type="button" className="btn btn-outline-success" onClick={handleExit}>Yes Save Changes</button>
                    </div>
                    </div>
                </div>
            </div>

            
            {abstractData && inputValue && (
                <ModalArticles
                    data={abstractData}
                    input={inputValue}
                    dataUpdate={prova}
                />
            )}

            <div
                className={`ContainerPersonalized ${
                    isMenuOptionsOpen ? "menu-options-open" : ""
                } ${isMenuGraphOpen ? "menu-graph-open" : ""}`}>

                <div className="backGround">
                    <button
                        className="menuOptionsButton"
                        onClick={toggleMenuOptions}
                    >
                        <IoMdSettings /> 
                    </button>
                    <MenuOptions
                        isOpen={isMenuOptionsOpen} //menu should be open
                        onClose={() => setMenuOptionsOpen(false)} //menu should be closer
                        menuItems={menuItems}
                        onItemClick={setMenuOptionsOpen}
                    />

                    {lastModifiedVariable === selectedNode &&
                        selectedNode &&
                        isMenuGraphOpen && (
                            <MenuGraph
                                isOpen={true}
                                onClose={() => setMenuGraphOpen(false)}
                                menuItems={
                                    selectedNode == null
                                        ? setMenuGraphOpen(false)
                                        : graph.getNodeAttributes(selectedNode)
                                }
                                mySelectedMenuNode={selectedNode}
                                setMySelectedMenuNode={setSelectedNode}
                                myMenuGraph={graph}
                                setMyMenuGraph={setGraph}
                            />
                        )}

                    {lastModifiedVariable === selectedEdge &&
                        selectedEdge &&
                        isMenuGraphOpen && (
                            <MenuGraph
                                isOpen={true}
                                onClose={() => setMenuGraphOpen(false)}
                                menuItems={
                                    selectedEdge == null
                                        ? setMenuGraphOpen(false)
                                        : graph.getEdgeAttributes(selectedEdge)
                                }
                                mySelectedMenuEdge={selectedEdge}
                                setMySelectedMenuEdge={setSelectedEdge}
                                myMenuGraph={graph}
                                setMyMenuGraph={setGraph}
                            />
                        )}

                    <div className="contentContainer">
                        <span className="title">Bio-Viber</span>
                        <p className="titleUnder">
                            <b>
                                Virtual Intelligent Biomedical Relation
                                Extraction Assistant
                            </b>
                        </p>
                    </div>

                    {/*<div className="inputContainer">
              
                    <input
                        id="inputGPT"
                        type="text"
                        className="inputText"
                        placeholder="✏️  Create your graph with a word..."
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                    <button className="enterButton" data-bs-toggle="modal" data-bs-target="#modalArticles" onClick={handleEnterClick} disabled={!inputValue.trim()}> 
                        <span><FaPaperPlane /></span>
                        </button>
                        </div>*/}

                    {/* THE MAIN ONE
                        enterButton ? (
                        <div className="input-group mb-3">
                            <input
                                id="inputGPT"
                                type="text"
                                className="inputText form-control"
                                aria-describedby="button-addon2"
                                placeholder="✏️ Google PubMed and select an article"
                                value={inputValue}
                                onChange={handleInputChange}
                            />
                            <button
                                type="button"
                                className="enterButton btn btn-outline-secondary"
                                id="button-addon2"
                                data-bs-toggle="modal"
                                data-bs-target="#modalArticles"
                                onClick={handleEnterClick}
                                disabled={!inputValue.trim()}
                            >
                                <span>
                                    <FaSearch />
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="input-group mb-3">
                            <input
                                id="inputGPT"
                                type="text"
                                className="inputText form-control"
                                aria-describedby="button-addon2"
                                placeholder="Press the reload button before making a new request."
                                value={
                                    "Press the button before making a new request"
                                }
                                readOnly
                            />
                            <button
                                type="button"
                                className="enterButton btn btn-outline-secondary"
                                id="button-addon2"
                                onClick={refreshPage}
                            >
                                <span>
                                    <FaRedo />
                                </span>
                            </button>
                        </div>
                    )*/} 

                    <div className="containerGraph" >
                        <SigmaContainer
                            style={{ flex: 1, marginBottom: "9vh", height:'90%', border: '1px solid #ccc', borderRadius: '.3rem' }}
                            render="canvas"
                            settings={{
                                renderNodeHidden: (node) =>
                                    node.hidden === true,
                                    labelSizeRatio: 0.2,
                                    renderEdgeLabels: true,
                                    labelThreshold: 0,
                                    defaultEdgeType: "straight",
                                    allowInvalidContainer: true,
                                    enableEdgeEvents: true,
                                    enableNodeEvents: true,
                                    enableEdgeHovering: true,
                                    enableNodeHovering: true,
                                    EdgeCurvedArrowProgram: true,
                                    edgeProgramClasses: {
                                        line: EdgeLineProgram,
                                        curved: EdgeCurvedArrowProgram,
                                        straight: EdgeArrowProgram,
                                    },
                                //defaultNodeType: "image",
                                /*nodeProgramClasses: {
                                image: NodeImageProgram,
                            },*/
                                            /*edgeLabelSize: 50,
                            defaultLabelAlignment: 'left',
                            labelVisibility: (node, context, settings) => {
                                return context.graph.nodes().length <= 5;
                            },*/
                                            //labelPosition: 'bottom'
                            }}>

                            <LoadGraph
                                confidenceEdges={setConfidenceEdges}
                                markedMyEdges={setMarkedEdges}
                                myDisplayGraph={graph}
                                mySetDisplayGraph={setGraph}
                                graphInfo={setInformationGraph}
                                viewMenuAttribute={setMenuGraphOpen}
                                mySelectedNodeDisplay={setSelectedNode}
                                mySelectedEdgeDisplay={setSelectedEdge}
                                colorOfMyFamilys={familyColor}
                                changeColorOfMyFamilys={setFamilyColor}
                                viewNodesFamily={viewFamily}
                                confidenceLevel={confidenceLevel}
                                labelOfMyEdge={setFamilyLabel}
                                familyWithConfidence={setFamilyConfidence}
                                viewEdgeFamily={viewEdges}
                                acceptEdgeFamily={acceptConfidentEdges}
                                pressButtonEdge={pressButton}
                                numerOfEdgeFamily={setNumberFamilyConfidence}
                                fa2Layout={selectedLayout}
                                nodeSize={selectedNodeSize}
                                edgeSize={selectedEdgeSize}
                                edgeForDisplay={setViewEdgesForFile}
                                allEdgeToPass={setViewEdgesNoConfForFile}
                                dataForGraph={updateData}
                                setRatedEdges={setRatedEdgeIds}
                                ratedEdges={ratedEdgeIds}
                                visMethod={visMethod}
                                ref={loadRef}
                            />
                            <ControlsContainer position="top-right">
                                <ZoomControl />
                                <FullScreenControl />
                            </ControlsContainer>
                        </SigmaContainer>

                        {/*<div style={{position: 'absolute', top: -10, right: 5, width: '25%', padding: '1em'}}>
                        <button style={{background: selectedLayout === "fa2" ? "white" : "rgba(94, 91, 91, 0.719)", opacity: 0.9, border: selectedLayout === "fa2" ? "1px solid rgba(94, 91, 91, 0.719)" : "none", color: selectedLayout === "fa2" ? "rgba(94, 91, 91, 0.719)" : "white", fontSize: '80%', width: '49%', right: 0, padding: '1em', position: 'absolute', cursor: 'pointer', borderRadius: '5px', transition: 'all 0.3s ease'}} onClick={() => handleLayoutChange()} >{selectedLayout === "fa2" ? "Default" : "Force Atlas 2"}</button>  
                        </div>*/}

                        <div className="graph-footer"
                            style={{
                                position: "absolute",
                                bottom: 10,
                                padding: "1%",
                                marginRight: "2rem",
                                marginLeft: "2rem",
                                background: "white",
                                border: "1px solid #ccc",
                                textAlign: "center",
                                borderRadius: "1vh",
                                boxShadow: "0 5px 4px rgba(0, 0, 0, 0.2)",
                                zIndex: 1,
                                opacity: 0.6,
                            }}>
                            <div className="conf-container">
                                {<span>confidence edges color</span>}
                                <table>
                                    <tbody>
                                        <tr>
                                            {[
                                                { label: "Implausible", color: "red" },
                                                { label: "Uncertain", color: "goldenrod" },
                                                { label: "Plausible", color: "green" },
                                            ].map((region, index) => (
                                                <td
                                                    key={index}
                                                    style={{
                                                        backgroundColor: region.color,
                                                        width: "90px",
                                                        textAlign: "center",
                                                        color: "black",
                                                    }}
                                                >
                                                    {region.label}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                   {/* {<div className="container py-5 mx-5">
                        <div className="row mx-5 px-5">
                            <div className="col-6">
                                <h2 className="fw-semibold fs-5">
                                    Do you want to save accepted edges?
                                </h2>
                                <button
                                    className="btn btn-success p-3 fw-semibold"
                                    onClick={handleButtonClick}
                                >
                                    Save marked edges
                                </button>
                            </div>

                            <div className="col-6 text-end">
                                <h2 className="fw-semibold fs-5">
                                    Do you want to enhance the schema?
                                </h2>
                                <button
                                    className="btn btn-warning p-3 fw-semibold"
                                    onClick={handleButtonClickEnhance}
                                >
                                    Enhance schema
                                </button>
                            </div>
                        </div>
                    </div>} */}
                    
                    { graph_num > 1 ? (
                        <nav aria-label="Page navigation">
                            <ul className="pagination">
                                <li className="page-item" ><a className="page-link" href="" onClick={handlePrevPage}>Previous</a></li>
                                {Object.keys(Data).map((k, index) => <li key={index} className={"page-item" + (index+1 === currPage? " active" : "")}><a className="page-link" href="" onClick={handleNumPage}>{index + 1}</a></li>)}
                                <li className="page-item"><a className="page-link" href="" onClick={handleNextPage}>Next</a></li>
                            </ul>
                        </nav>
                        ) : 
                        (<></>)
                    }

                    {typeof abstract === 'string' && (
                    <div className="summary-container">
{/*
                        {Array.isArray(parts) && Array.isArray(ontologyLabels) && isPred ? (
                            <p>
                                {parts.map((part, index) =>
                                    ontologyLabels.some(ol => ol.toLowerCase() === part.toLowerCase()) ? (
                                    <span className='highlight' key={index} title={nodeType[part].join(' | ').replaceAll('_', ' ')} style={{ backgroundColor: colorMap[part]}}>
                                        {part}
                                    </span>
                                    ) : uniquePred.some(up => up.toLowerCase() === part.toLowerCase()) ? (
                                    <span key={index} style={{ backgroundColor: '#FFED29' }}>
                                        {part}
                                    </span>
                                    ) : (
                                    part
                                    )
                                )}
                            </p>
                        ) : 
                        Array.isArray(parts) && Array.isArray(ontologyLabels) && !isPred ? (
                            <p>
                                {parts.map((part, index) =>
                                    ontologyLabels.some(ol => ol.toLowerCase() === part.toLowerCase()) ? (
                                    <span className='highlight' key={index} title={nodeType[part].join(' | ').replaceAll('_', ' ')} style={{ backgroundColor: colorMap[part]}}>
                                        {part}
                                    </span>
                                    ) : (
                                    part
                                    )
                                )}
                            </p>
                        ) : 
                        (
                            <p>
                                {parts.map((part) => part)}
                            </p>
                        )
                        
                        } */}
                        {Array.isArray(italicSpans) ?
                            (
                                <p>
                                    {renderStyledText(abstract , italicSpans, ontologyLabels)}
                                </p>
                            ) :
                            (
                                <p>not rendered!</p>
                            )
                        }
                    </div>
                    )}

                    <div className="exit-container">
                        <button type="button" className="btn btn-danger" data-bs-toggle='modal' data-bs-target='#exitModal'>
                            Exit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DisplayGraph;
